import { getApiUrl } from "../environment"
import { makeRequest } from "../../lib/utils"
import { formatHoursDuration } from "../../components/DeltaTSelector/deltaTScale"
import { catchMessage } from "./errors"

/**
 * Koppeling met de Darwin Core-helft van de API: alle `/eggspedition/*`-endpoints.
 *
 * Waarom een apart bestand en niet erbij in `functions.js`: de back-end heeft
 * dezelfde splitsing gemaakt (`routers/eggspedition_*.py` naast de bestaande
 * routers, schema `eggspedition` naast `visai`). De oude endpoints blijven
 * ongewijzigd draaien; wie een pagina omzet, wisselt van import in plaats van
 * een functie te herschrijven. Er is dus geen enkele functie in `functions.js`
 * aangeraakt.
 *
 * Het verschil met de oude helft dat je in de UI merkt:
 *
 * * Een waarneming is genest: een **event** (wanneer + waar) met daaronder
 *   **occurrences** (de specimens). `event.occurrences[0]` is wat in de oude
 *   API het platte upload-record was.
 * * Een later moment van hetzelfde ei is een eigen event onder
 *   `event.child_events`, met een eigen `event_date`, de "egg trail". Wat de
 *   momenten verbindt is `organism_id`, niet het event.
 * * Tijden zijn ISO-strings met tijdzone, geen epoch-getallen.
 * * Foutmeldingen komen als `{ "detail": "..." }`; `parseDetail` haalt daar de
 *   leesbare tekst uit, inclusief de hint die de databasefunctie meegeeft.
 *
 * Foto's: `multimedia[].file_url` is rechtstreeks bruikbaar als `<img src>`,
 * het download-endpoint vraagt sinds v1.6 geen token meer, net als het oude.
 * `EggspeditionAttachmentBlobUrl` staat er voor het geval dat ooit terugdraait.
 */

const eggspeditionUrl = (path, params) => {
  const url = `${getApiUrl()}/eggspedition${path}`
  if (!params) return url

  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") search.set(key, String(value))
  })

  return search.size ? `${url}?${search}` : url
}

// FastAPI zet de uitleg in `detail`. Bij een 400 uit een databasefunctie staat
// daar de melding + de HINT die zegt wat er wél mag; die is voor de gebruiker
// het bruikbare deel. Bij een 422 is `detail` een lijst met veldfouten.
// Geëxporteerd omdat de `/ml`-endpoints (zie `mlTraining.js`) dezelfde foutvorm gebruiken.
export const parseDetail = (raw) => {
  try {
    const detail = JSON.parse(raw)?.detail
    if (typeof detail === "string") return detail
    if (Array.isArray(detail)) return detail.map(e => `${(e.loc || []).slice(1).join(".")}: ${e.msg}`).join("; ")
    if (detail) return JSON.stringify(detail)
  } catch (_) {
    // geen JSON, dan is de ruwe tekst het beste wat we hebben
  }
  return typeof raw === "string" && raw ? raw : "Request failed"
}

const JSON_HEADERS = { "Content-Type": "application/json" }

// De twee waarden die het scientist-formulier in `sampling_protocol` kan zetten. Alles wat daar
// verder in staat is de rol-default van de API en betekent "niet ingevuld".
const SPAWNING_METHODS = ["Spawning directly observed", "Spawning not observed"]

/**
 * Eén request. Geeft het geparste antwoord terug, of `undefined` als er iets
 * misging, dan is de melding al via `catchMessage` naar de gebruiker gegaan.
 * Dat is dezelfde afspraak als in `functions.js`, zodat aanroepende code niet
 * hoeft te weten uit welke helft van de API iets komt.
 */
const eggspeditionRequest = async (dispatch, functionName, method, { signal, token, path, params, query, body, headers }) => {
  try {
    const response = await makeRequest(
      eggspeditionUrl(path, query), functionName, method, signal, token, params || {}, body, headers)

    if (!response) throw new Error("No response")
    if (response.exception !== undefined) throw new Error(parseDetail(response.exception))

    return typeof response === "string" ? JSON.parse(response) : response
  } catch (error) {
    dispatch(catchMessage({ exception: error }, functionName))
  }
}

// ---------------------------------------------------------------------------
// Waarnemingen aanmaken en ophalen
// ---------------------------------------------------------------------------

/**
 * Nieuwe waarneming. `parameters.specialist` is de rol (diver/fisher/aquarist/
 * scientist), `body` volgt EggspeditionUploadBody met `attachments: [{ file }]`.
 * Antwoord: het volledige geneste event.
 */
export const EggspeditionUpload = (signal, token, parameters, body) => async (dispatch) => {
  const { attachments = [], ...rest } = body || {}

  const formData = new FormData()
  formData.append("body", JSON.stringify(rest))
  attachments.forEach(attachment => formData.append("attachments", attachment.file || attachment))

  return eggspeditionRequest(dispatch, "EggspeditionUpload", "POST", {
    signal, token, path: `/upload/${parameters.specialist}`, params: parameters, body: formData
  })
}

/**
 * Eigen waarnemingen, nieuwste eerst. Antwoord is een envelope
 * `{ total, limit, offset, items }`; `items` zijn volledig geneste events.
 * Vervolgmomenten staan NIET als losse waarneming in de lijst, die hangen bij
 * hun eigen waarneming onder `child_events`.
 */
export const EggspeditionUploadsGet = (signal, token, parameters = {}, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionUploadsGet", "GET", {
    signal, token, path: "/uploads", params: parameters, body,
    query: { limit: parameters.limit, offset: parameters.offset }
  })
}

/** Waarnemingen van één rol, zelfde envelope als `EggspeditionUploadsGet`. */
export const EggspeditionUploadsByRoleGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionUploadsByRoleGet", "GET", {
    signal, token, path: `/uploads/${parameters.upload_role}`, params: parameters, body,
    query: { limit: parameters.limit, offset: parameters.offset }
  })
}

/** De grootste pagina die de API toestaat. */
export const EGGSPEDITION_PAGE_SIZE = 200

// Zoveel pagina's halen we hooguit op: 5.000 waarnemingen. Een noodrem voor het geval `total` niet
// klopt of het endpoint blijft doorgeven, niet een grens die in de praktijk gehaald hoort te worden.
const EGGSPEDITION_MAX_PAGES = 25

/**
 * Alle pagina's van een gepagineerd endpoint achter elkaar ophalen.
 *
 * De overzichten groeperen, filteren, sorteren en exporteren allemaal client-side. Met echte
 * paginering zou "selecteer alles" of een CSV-export ineens alleen over de zichtbare pagina gaan,
 * dus halen we alles op, één request per 200 waarnemingen.
 *
 * `fetchPage(query)` levert per aanroep een envelope `{ total, items }`; komt die niet (een
 * mislukte of afgebroken request), dan stopt de lus met wat er wél binnen is.
 *
 * Geeft `{ total, items, truncated }`. `truncated` zegt of er meer is dan wat je terugkrijgt, dan
 * is de lijst onvolledig en hoort de gebruiker dat te weten.
 *
 * Geëxporteerd om te kunnen testen zonder requests: `dispatch` en `fetchPage` zijn de enige twee
 * dingen die het naar buiten doet.
 */
export const eggspeditionAllPages = async (dispatch, fetchPage) => {
  const items = []
  let total = 0

  for (let page = 0; page < EGGSPEDITION_MAX_PAGES; page++) {
    const json = await dispatch(fetchPage({ limit: EGGSPEDITION_PAGE_SIZE, offset: items.length }))

    if (!json) return { total: Math.max(total, items.length), items, truncated: true }

    const pageItems = Array.isArray(json.items) ? json.items : []
    total = typeof json.total === "number" ? json.total : items.length + pageItems.length
    items.push(...pageItems)

    if (pageItems.length === 0 || items.length >= total) break
  }

  return { total, items, truncated: items.length < total }
}

/**
 * Alle waarnemingen als kaartpunt: `{ event_id, lat, lng, upload_role, event_date,
 * occurrence_count, moment_count }`, als platte array en niet gepagineerd.
 *
 * Het enige `/eggspedition/*`-endpoint **zonder token**: de kaart op de voorpagina moet er staan
 * voordat iemand inlogt. Daarmee is het publieke data, dus de API geeft alleen punten van melders
 * die publicatie hebben toegestaan, en aquarist-waarnemingen (geen coordinaten) staan er niet in.
 * Het aantal punten is dus kleiner dan het aantal waarnemingen.
 */
export const EggspeditionMapPointsGet = (signal, parameters = {}, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionMapPointsGet", "GET", {
    signal, token: null, path: "/map-points", params: parameters, body
  })
}

/**
 * Eigen spawning-observaties waar een volgend ei bij kan: op `dive_site_id`, paaien waargenomen,
 * `event_date` tussen `start` en `end`. Een lijst van volledige events, oudste eerst.
 */
export const EggspeditionSpawningBatchesGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionSpawningBatchesGet", "GET", {
    signal, token, path: "/spawning-batches", params: parameters, body,
    query: { dive_site_id: parameters.dive_site_id, start: parameters.start, end: parameters.end }
  })
}

/** Alle eigen waarnemingen, over alle pagina's heen. Zie `eggspeditionAllPages`. */
export const EggspeditionUploadsAllGet = (signal, token, parameters = {}, body) => async (dispatch) =>
  eggspeditionAllPages(dispatch, (query) =>
    EggspeditionUploadsGet(signal, token, { ...parameters, ...query }, body))

/** Alle waarnemingen van één rol, over alle pagina's heen. */
export const EggspeditionUploadsByRoleAllGet = (signal, token, parameters, body) => async (dispatch) =>
  eggspeditionAllPages(dispatch, (query) =>
    EggspeditionUploadsByRoleGet(signal, token, { ...parameters, ...query }, body))

/**
 * Eén waarneming met alles eronder: occurrences, media, identificaties, DNA,
 * metingen én `child_events` (de egg trail). Dit is de databron die `EggTrail`
 * nodig heeft; `eggspeditionTrailOccurrences` maakt er de platte reeks van.
 */
export const EggspeditionEventGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionEventGet", "GET", {
    signal, token, path: `/event/${parameters.event_id}`, params: parameters, body
  })
}

/**
 * Waarneming corrigeren. Alleen de meegestuurde velden veranderen. De rol, de
 * eigenaar en de identificatievelden liggen vast; een onbekend veld geeft 400
 * met een melding die zegt wat er wél mag, in plaats van stil te mislukken.
 */
export const EggspeditionEventPatch = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionEventPatch", "PATCH", {
    signal, token, path: `/event/${parameters.event_id}`, params: parameters, body, headers: JSON_HEADERS
  })
}

/**
 * Waarneming verwijderen, inclusief de hele egg trail en de bestanden.
 * Vereist de rol `admin`. Geen undo.
 */
export const EggspeditionEventDelete = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionEventDelete", "DELETE", {
    signal, token, path: `/event/${parameters.event_id}`, params: parameters, body
  })
}

// ---------------------------------------------------------------------------
// Occurrences: nog een specimen, of hetzelfde specimen later
// ---------------------------------------------------------------------------

/**
 * Nog een specimen in een bestaande waarneming: zelfde plek, **zelfde moment**,
 * ander ei, één duik die meer dan één vondst opleverde.
 *
 * Dit is NIET de manier om een ei door de tijd te volgen: alles wat hier
 * bijkomt draagt de datum van de oorspronkelijke waarneming, en occurrences die
 * een event delen betekenen voor een afnemer van de export "verschillende
 * individuen, hetzelfde moment". Gebruik daarvoor `EggspeditionFollowUpAdd`.
 */
export const EggspeditionOccurrenceAdd = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionOccurrenceAdd", "POST", {
    signal, token, path: `/event/${parameters.event_id}/occurrence`, params: parameters, body, headers: JSON_HEADERS
  })
}

/**
 * Hetzelfde specimen op een **later moment**, één stap in de egg trail.
 *
 * `parameters.occurrence_id` is de occurrence die je volgt. In de body precies
 * één van `delta_hours` (uren ná het oorspronkelijke waarneemmoment) of
 * `event_date`. Velden die je weglaat erven van de gevolgde occurrence.
 *
 * De API maakt er een eigen event van, op dezelfde plek, met `parent_event_id`
 * naar de oorspronkelijke waarneming, en zet op beide momenten hetzelfde
 * `organism_id`. Antwoord: `{ occurrence, event_id, parent_event_id,
 * event_date, organism_id, source_occurrence_id }`.
 *
 * Foto's gaan er los achteraan, tegen `occurrence.occurrence_id`; gebruik
 * `EggspeditionFollowUpCreate` als je beide in één handeling wil.
 */
export const EggspeditionFollowUpAdd = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionFollowUpAdd", "POST", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}/follow-up`, params: parameters, body, headers: JSON_HEADERS
  })
}

/** Eén occurrence met media, identificatiegeschiedenis, DNA en metingen. */
export const EggspeditionOccurrenceGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionOccurrenceGet", "GET", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}`, params: parameters, body
  })
}

/**
 * Occurrence corrigeren. De identificatievelden kunnen hier niet: die spiegelen
 * één rij uit de identificatiegeschiedenis. Voor een andere soortnaam voeg je
 * een identificatie toe. `organism_id` is evenmin te wijzigen, dat zou een
 * trail in tweeën knippen.
 */
export const EggspeditionOccurrencePatch = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionOccurrencePatch", "PATCH", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}`, params: parameters, body, headers: JSON_HEADERS
  })
}

/** Categorie van een bijlage corrigeren (fish egg / larvae). Melder of admin. */
export const EggspeditionAttachmentPatch = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionAttachmentPatch", "PATCH", {
    signal, token, path: `/attachment/${parameters.multimedia_id}`, params: parameters, body, headers: JSON_HEADERS
  })
}

/** Occurrence verwijderen. Vereist de rol `admin`. */
export const EggspeditionOccurrenceDelete = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionOccurrenceDelete", "DELETE", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}`, params: parameters, body
  })
}

// ---------------------------------------------------------------------------
// Bijlagen
// ---------------------------------------------------------------------------

/**
 * Eén foto onder een bestaande occurrence. Eén bestand per aanroep; dat is wat
 * het endpoint accepteert. `parameters.attachment_category` en `title` zijn
 * optioneel.
 */
export const EggspeditionAttachmentAdd = (signal, token, parameters, body) => async (dispatch) => {
  const file = body?.file || body

  const formData = new FormData()
  formData.append("file", file)
  if (parameters.attachment_category) formData.append("attachment_category", parameters.attachment_category)
  if (parameters.title || file?.name) formData.append("title", parameters.title || file.name)

  return eggspeditionRequest(dispatch, "EggspeditionAttachmentAdd", "POST", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}/attachment`,
    params: { occurrence_id: parameters.occurrence_id }, body: formData
  })
}

/** Metadata van één bijlage, inclusief `file_url`. */
export const EggspeditionAttachmentGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionAttachmentGet", "GET", {
    signal, token, path: `/attachment/${parameters.multimedia_id}`, params: parameters, body
  })
}

/**
 * Blob-URL van het bestand van een bijlage, of `null`.
 *
 * Nodig omdat het download-endpoint van deze helft een bearer-token vereist,
 * anders dan het oude `/attachment/{upload_id}/{attachment_id}/download`, dat
 * open staat. `file_url` rechtstreeks in een `<img src>` geeft hier dus een 401.
 * Roep `URL.revokeObjectURL` aan als de afbeelding weg is.
 */
export const EggspeditionAttachmentBlobUrl = (signal, token, parameters) => async () => {
  try {
    const response = await fetch(eggspeditionUrl(`/attachment/${parameters.multimedia_id}/download`), {
      headers: { "Authorization": `Bearer ${token}` },
      signal
    })
    if (!response.ok) return null

    return URL.createObjectURL(await response.blob())
  } catch (_) {
    return null
  }
}

// ---------------------------------------------------------------------------
// Identificaties, DNA en metingen
// ---------------------------------------------------------------------------

/**
 * Identificatiegeschiedenis van een occurrence, nieuwste eerst. `is_current`
 * markeert de identificatie die de occurrence nu weerspiegelt.
 */
export const EggspeditionIdentificationsGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionIdentificationsGet", "GET", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}/identifications`, params: parameters, body
  })
}

/**
 * Identificatie toevoegen. Altijd een toevoeging, nooit een overschrijving:
 * `became_current` in het antwoord zegt of deze de geaccepteerde is geworden.
 * Dat gebeurt niet als er al een bepaling met meer gezag ligt (dna > expert >
 * photo-review > ai > reporter).
 */
export const EggspeditionIdentificationAdd = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionIdentificationAdd", "POST", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}/identification`, params: parameters, body, headers: JSON_HEADERS
  })
}

/** DNA-rijen van een occurrence. */
export const EggspeditionDnaGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionDnaGet", "GET", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}/dna`, params: parameters, body
  })
}

/**
 * DNA-resultaat toevoegen. Vereist de rol `scientist`. Met een `scientific_name`
 * erbij wordt het ook een identificatie met methode `dna`, het hoogste gezag.
 */
export const EggspeditionDnaAdd = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionDnaAdd", "POST", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}/dna`, params: parameters, body, headers: JSON_HEADERS
  })
}

/**
 * DNA-resultaat corrigeren. Vereist de rol `admin`. De soort hoort er niet bij: een andere soort is
 * een nieuwe identificatie. Geeft de occurrence terug.
 */
export const EggspeditionDnaPatch = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionDnaPatch", "PATCH", {
    signal, token, path: `/dna/${parameters.dna_id}`, params: parameters, body, headers: JSON_HEADERS
  })
}

/** eMoF-metingen van een occurrence, in long format. */
export const EggspeditionMeasurementsGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionMeasurementsGet", "GET", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}/measurements`, params: parameters, body
  })
}

/** Meetresultaat opslaan bij een occurrence (EggspeditionMeasurementBody). */
export const EggspeditionMeasurementStore = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionMeasurementStore", "POST", {
    signal, token, path: `/occurrence/${parameters.occurrence_id}/measurement`, params: parameters, body, headers: JSON_HEADERS
  })
}

// ---------------------------------------------------------------------------
// Beeld: soortvoorspelling en meting op een opgeslagen foto
// ---------------------------------------------------------------------------

/**
 * Soortvoorspelling op een opgeslagen foto.
 *
 * Zonder `unknown_threshold`: die drempel hoort bij het model en de API neemt die van het actieve
 * model (zie api/eggspedition_modelhertraining_plan.md, besluit 6). Hij staat nog wel in het
 * antwoord, als `raw_result.unknown_threshold`, zodat de uitslag te verklaren blijft.
 */
export const EggspeditionPredictionGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionPredictionGet", "GET", {
    signal, token, path: `/predict/${parameters.multimedia_id}`, params: parameters, body,
    query: {
      top_k: parameters.top_k,
      egg_diameter_um: parameters.egg_diameter_um,
      oil_diameter_um: parameters.oil_diameter_um
    }
  })
}

/**
 * Meten op een opgeslagen foto, geen her-upload nodig. Met `store: false` wordt
 * er niets vastgelegd, handig tijdens het handmatig bijstellen van de cirkels.
 */
export const EggspeditionMeasureAttachment = (signal, token, parameters, body) => async (dispatch) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 90_000)

  try {
    return await eggspeditionRequest(dispatch, "EggspeditionMeasureAttachment", "GET", {
      signal: signal || controller.signal, token,
      path: `/measure/${parameters.multimedia_id}`, params: parameters, body,
      query: {
        scale_um: parameters.scale_um, view_type: parameters.view_type, globules: parameters.globules,
        store: parameters.store === undefined ? undefined : String(parameters.store),
        egg_cx_px: parameters.egg_cx_px, egg_cy_px: parameters.egg_cy_px, egg_r_px: parameters.egg_r_px,
        oil_cx_px: parameters.oil_cx_px, oil_cy_px: parameters.oil_cy_px, oil_r_px: parameters.oil_r_px
      }
    })
  } finally {
    clearTimeout(timeoutId)
  }
}

/** Blob-URL van de geannoteerde JPEG van een opgeslagen foto, of `null`. */
export const EggspeditionMeasureAnnotatedUrl = (signal, token, parameters) => async () => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 90_000)

  try {
    const response = await fetch(eggspeditionUrl(`/measure/${parameters.multimedia_id}/annotated`, {
      scale_um: parameters.scale_um, view_type: parameters.view_type, globules: parameters.globules,
      egg_cx_px: parameters.egg_cx_px, egg_cy_px: parameters.egg_cy_px, egg_r_px: parameters.egg_r_px,
      oil_cx_px: parameters.oil_cx_px, oil_cy_px: parameters.oil_cy_px, oil_r_px: parameters.oil_r_px
    }), {
      headers: { "Authorization": `Bearer ${token}` },
      signal: signal || controller.signal
    })
    if (!response.ok) return null

    return URL.createObjectURL(await response.blob())
  } catch (_) {
    return null
  } finally {
    clearTimeout(timeoutId)
  }
}

// ---------------------------------------------------------------------------
// Duiklocaties
// ---------------------------------------------------------------------------

/**
 * Alle duiklocaties, alfabetisch op naam. Geen parameters, geen paginering, het zijn er ruim
 * 8000 en dat is bewust één antwoord, net als bij de kaartpunten.
 *
 * Let op de dubbele betekenis van "dive_site_id" (zie api/eggspedition_dive_site_specimen_nummer.md
 * §3): **binnen deze lijst** is `dive_site_id` de leesbare 4-lettercode (`KREI`) en is `id` de uuid.
 * Op een event/upload is `dive_site_id` juist die uuid. Wat je terugstuurt is dus `id`.
 */
export const EggspeditionDiveSitesGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionDiveSitesGet", "GET", {
    signal, token, path: "/dive-sites", params: parameters, body
  })
}

/**
 * Het hoeveelste specimen dit kalenderjaar op deze duiklocatie zou zijn.
 *
 * Nadrukkelijk een **preview**: er wordt niets vastgelegd of gereserveerd. Twee melders die
 * tegelijk op dezelfde locatie bezig zijn kunnen hetzelfde getal terugkrijgen; het definitieve
 * nummer legt de API pas vast bij het aanmaken van de waarneming, en komt daarna terug als
 * `specimen_number` op het event.
 *
 * `event_date` hoort mee zodra het formulier een datum in het verleden toestaat, en dat doet het:
 * je uploadt een foto van een eerdere duik. Het nummer telt per kalenderjaar, dus zonder die datum
 * krijg je de stand van vandaag terug en klopt de preview niet met wat er straks wordt vastgelegd.
 */
/**
 * Hetzelfde nummer, maar voor een tank in plaats van een duiklocatie: het hoeveelste specimen dit
 * kalenderjaar in déze tank zou zijn. Vervolgmomenten tellen niet mee, net als bij de duiklocatie.
 *
 * Ook hier een preview zonder reservering, met hetzelfde voorbehoud.
 */
export const EggspeditionTankSpecimenNumberGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionTankSpecimenNumberGet", "GET", {
    signal, token, path: `/tank/${parameters.tank_id}/specimen-number`, params: parameters, body,
    query: {
      event_date: parameters.event_date
        ? new Date(parameters.event_date).toISOString()
        : undefined
    }
  })
}

export const EggspeditionSpecimenNumberGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionSpecimenNumberGet", "GET", {
    signal, token, path: `/dive-site/${parameters.dive_site_id}/specimen-number`, params: parameters, body,
    query: {
      event_date: parameters.event_date
        ? new Date(parameters.event_date).toISOString()
        : undefined
    }
  })
}

// ---------------------------------------------------------------------------
// Tanks en export
// ---------------------------------------------------------------------------

export const EggspeditionTanksGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionTanksGet", "GET", {
    signal, token, path: "/tanks", params: parameters, body
  })
}

export const EggspeditionTankGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionTankGet", "GET", {
    signal, token, path: `/tank/${parameters.tank_id}`, params: parameters, body
  })
}

/** Tank aanmaken of bijwerken; met `id` in de body is het een update. */
export const EggspeditionTankSave = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionTankSave", "POST", {
    signal, token, path: "/tank/create", params: parameters, body, headers: JSON_HEADERS
  })
}

/** Bestaande tank bijwerken. De body is de hele tank: een weggelaten veld wordt leeggemaakt. */
export const EggspeditionTankUpdate = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionTankUpdate", "PUT", {
    signal, token, path: `/tank/${parameters.tank_id}`, params: parameters, body, headers: JSON_HEADERS
  })
}

/** Tank verwijderen. Een tank die in gebruik is geeft 409. */
export const EggspeditionTankDelete = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionTankDelete", "DELETE", {
    signal, token, path: `/tank/${parameters.tank_id}`, params: parameters, body
  })
}

/** Eieren waarvan het paaien direct is waargenomen, voor de catalogus. Vereist `catalog-viewer`. */
export const EggspeditionCatalogSpawningGet = (signal, token) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionCatalogSpawningGet", "GET", { signal, token, path: "/catalog/spawning-observed" })
}

/** Alle soortrecords (WoRMS-taxon met wetenschappelijke en Engelse naam, en hun schrijfwijzen). */
export const EggspeditionSpeciesGet = (signal, token) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionSpeciesGet", "GET", { signal, token, path: "/species" })
}

/** Een WoRMS-taxon vastleggen of bijwerken als soortrecord; `body` volgt EggspeditionSpeciesBody. */
export const EggspeditionSpeciesPut = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionSpeciesPut", "PUT", {
    signal, token, path: `/species/${parameters.aphia_id}`, params: parameters, body, headers: JSON_HEADERS
  })
}

/** Beschikbare Darwin Core Archive-bestanden met hun rijaantallen. */
export const EggspeditionExportCoresGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionExportCoresGet", "GET", {
    signal, token, path: "/export", params: parameters, body
  })
}

/** Eén Darwin Core Archive-bestand als rijen met officiële termnamen. */
export const EggspeditionExportGet = (signal, token, parameters, body) => async (dispatch) => {
  return eggspeditionRequest(dispatch, "EggspeditionExportGet", "GET", {
    signal, token, path: `/export/${parameters.core}`, params: parameters, body
  })
}

/**
 * Het volledige Darwin Core Archive als zip-blob: databestanden, een `meta.xml`
 * die elke kolom aan zijn Darwin Core-term koppelt, en een `eml.xml`.
 *
 * `parameters.event_ids` begrenst het tot een selectie waarnemingen; laat je hem
 * weg, dan is het de hele dataset. De vervolgmomenten van een geselecteerde
 * waarneming gaan altijd mee, die horen bij hetzelfde organisme.
 *
 * Geeft `null` terug als het misging; de melding staat dan al op het scherm. Een
 * selectie waarvan niets gepubliceerd mag worden geeft een 409 met uitleg, geen
 * leeg archief.
 */
export const EggspeditionExportArchive = (signal, token, parameters = {}) => async (dispatch) => {
  const search = new URLSearchParams()
  for (const id of parameters.event_ids || []) search.append("event_id", id)

  const url = `${getApiUrl()}/eggspedition/export/archive/download${search.size ? `?${search}` : ""}`

  try {
    const response = await fetch(url, { headers: { "Authorization": `Bearer ${token}` }, signal })
    if (!response.ok) throw new Error(parseDetail(await response.text()))

    return await response.blob()
  } catch (error) {
    dispatch(catchMessage({ exception: error }, "EggspeditionExportArchive"))
    return null
  }
}

// ---------------------------------------------------------------------------
// Samengesteld: een vervolgmoment mét zijn foto's
// ---------------------------------------------------------------------------

/**
 * Eén vervolgmoment vastleggen en er meteen de foto's onder hangen.
 *
 * Twee stappen, want dat is wat de API aanbiedt: het moment moet bestaan
 * voordat er een occurrence_id is om foto's aan te koppelen, en foto's gaan één
 * per aanroep. De foto's worden op volgorde geüpload; mislukt er één, dan
 * blijft het moment gewoon staan en staat het bestand in `failed_files`. Dat is
 * beter dan alles terugdraaien: het moment zelf is de waarneming, de foto is er
 * bewijs bij en kan later alsnog via `EggspeditionAttachmentAdd`.
 *
 * `parameters`: `{ occurrence_id, attachment_category }`
 * `body`: de velden van EggspeditionFollowUpBody plus `files: [{ file }]`.
 *
 * Antwoord: het antwoord van het follow-up-endpoint, aangevuld met
 * `multimedia` (de geslaagde uploads) en `failed_files` (namen).
 */
export const EggspeditionFollowUpCreate = (signal, token, parameters, body) => async (dispatch) => {
  const { files = [], ...followUp } = body || {}

  const result = await dispatch(EggspeditionFollowUpAdd(signal, token, parameters, followUp))
  if (!result) return

  const occurrenceId = result.occurrence?.occurrence_id
  const multimedia = []
  const failedFiles = []

  for (const entry of files) {
    const file = entry?.file || entry
    const media = await dispatch(EggspeditionAttachmentAdd(signal, token, {
      occurrence_id: occurrenceId,
      attachment_category: parameters.attachment_category,
      title: file?.name
    }, { file }))

    if (media) multimedia.push(media)
    else failedFiles.push(file?.name || "?")
  }

  return { ...result, multimedia, failed_files: failedFiles }
}

// ---------------------------------------------------------------------------
// Hulpfuncties zonder request
// ---------------------------------------------------------------------------

/**
 * De egg trail van een event als één platte reeks occurrences, op tijdsvolgorde.
 *
 * Neemt het oorspronkelijke moment én `child_events` mee en zet er per
 * occurrence `event_date` en `delta_hours` (uren sinds het eerste moment) bij.
 * `egg_larvae_age` blijft de eigen bewoording van de melder; staat die leeg, dan
 * wordt de werkelijke ΔT als label ingevuld, zodat de trail altijd een
 * tijdsaanduiding toont.
 *
 * Bedoeld als voer voor `<EggTrail occurrences={...} />`.
 */
export const eggspeditionTrailOccurrences = (event) => {
  if (!event) return []

  const start = event.event_date ? new Date(event.event_date).getTime() : null

  return [event, ...(event.child_events || [])]
    .filter(moment => moment && moment.event_date)
    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
    .flatMap(moment => {
      const deltaHours = start === null
        ? null
        : Math.round((new Date(moment.event_date).getTime() - start) / 3_600_000)

      return (moment.occurrences || []).map(occurrence => ({
        ...occurrence,
        event_date: moment.event_date,
        delta_hours: deltaHours,
        egg_larvae_age: occurrence.egg_larvae_age
          || (deltaHours === null ? null : formatHoursDuration(deltaHours))
      }))
    })
}

// De eMoF-meting waarin de API het overeenkomstpercentage van een DNA-bepaling kwijt kan.
const DNA_PERCENT_IDENTITY = "dnaPercentIdentity"

/**
 * Alle DNA-resultaten van een waarneming, plat en op tijdsvolgorde.
 *
 * Drie dingen die je bij het lezen van de API-vorm makkelijk mist, en die deze functie afhandelt:
 *
 * 1. **DNA hangt aan een occurrence, als lijst.** Een waarneming met een egg trail heeft meer dan
 *    één occurrence, dus een resultaat op een later moment zit niet in `occurrences[0].dna`.
 *    Vandaar dat we, net als `eggspeditionTrailOccurrences`, over het event én zijn
 *    `child_events` lopen.
 * 2. **De soort staat niet op de DNA-rij.** Daar staat alleen `identification_id`; de naam zelf
 *    zit in de identificatiegeschiedenis van diezelfde occurrence. Zonder die koppeling toon je
 *    een sequentie zonder uitkomst.
 * 3. **Het percentage staat er ook niet op.** Dat is een eMoF-rij (`dnaPercentIdentity`) in
 *    `occurrence.measurements`.
 *
 * Bij punt 3 zit een grens: zo'n meting draagt een `occurrence_id` maar geen `dna_id`. Heeft één
 * occurrence meer dan één DNA-rij, dan is niet te zeggen welk percentage bij welke rij hoort,
 * dan blijft het leeg in plaats van gegokt.
 */
export const eggspeditionDnaResults = (event) => {
  if (!event) return []

  const start = event.event_date ? new Date(event.event_date).getTime() : null

  return [event, ...(event.child_events || [])]
    .filter(moment => moment)
    .sort((a, b) => new Date(a.event_date || 0) - new Date(b.event_date || 0))
    .flatMap(moment => {
      const deltaHours = (start === null || !moment.event_date)
        ? null
        : Math.round((new Date(moment.event_date).getTime() - start) / 3_600_000)

      return (moment.occurrences || []).flatMap(occurrence => {
        const rows = occurrence.dna || []

        const percentages = (occurrence.measurements || [])
          .filter(measurement => measurement.measurement_type === DNA_PERCENT_IDENTITY)
          .map(measurement => Number(measurement.measurement_value))
          .filter(value => Number.isFinite(value))

        // Alleen toe te wijzen als er precies één van elk is; zie de toelichting hierboven.
        const percentIdentity = (rows.length === 1 && percentages.length === 1) ? percentages[0] : null

        return rows.map(row => ({
          ...row,
          occurrence_id: row.occurrence_id || occurrence.occurrence_id,
          event_date: moment.event_date ?? null,
          delta_hours: deltaHours,
          scientific_name: (occurrence.identifications || [])
            .find(identification => identification.identification_id === row.identification_id)
            ?.scientific_name ?? null,
          percent_identity: percentIdentity
        }))
      })
    })
}

/**
 * Weer van de nieuwe naar de oude vorm: alleen `observed_at` heet daar `time`,
 * en dat is een epoch-getal. De rest van de sleutels is identiek.
 */
export const eggspeditionWeatherToLegacy = (weather) => {
  if (!weather) return null

  const { observed_at, ...rest } = weather
  return { ...rest, time: observed_at ? new Date(observed_at).getTime() : null }
}

/** En terug, voor een upload. */
export const eggspeditionWeatherFromLegacy = (weather) => {
  if (!weather) return null

  const { time, ...rest } = weather
  return { ...rest, observed_at: time ? new Date(time).toISOString() : null }
}

/**
 * Een genest event terugbrengen tot de platte vorm die de bestaande pagina's
 * lezen, dezelfde veldnamen als de oude `/upload/get`- en `/{rol}s`-endpoints.
 *
 * Dit is wat de omzetting klein houdt: `Account.js`, `Specialist.js` en
 * `createInitialValues` in `Form.js` hoeven niets te weten van events,
 * occurrences of Darwin Core-termen. Alleen de aanroep wisselt; de weergave
 * blijft staan.
 *
 * Wat er bovenop de oude vorm bij komt, voor wie het wél wil gebruiken:
 * `event_id`, `occurrence_id`, `organism_id`, `occurrences` (de egg trail, plat
 * en op tijdsvolgorde) en `event` (het onbewerkte antwoord).
 *
 * `attachments` bevat de media van de **eerste** occurrence, het equivalent van
 * wat de oude API onder een upload hing. De foto's van vervolgmomenten zitten in
 * `occurrences`, niet hier; anders zou een kaart per bijlage ineens ook de
 * latere momenten als losse kaarten tonen.
 */
/**
 * Hoe betrouwbaar de soortbepaling van een waarneming is, 1 tot 5 sterren, afgeleid uit wat er
 * over de hele waarneming bekend is (alle eieren en alle momenten van een egg trail):
 *
 *   5  DNA én het paaien gezien      4  het paaien gezien, of DNA
 *   3  diametermeting én AI          2  AI          1  alleen een foto
 *
 * Een duiker meldt een paaiing die hij zag, dus die telt als "paaien gezien"; een researcher
 * alleen als hij "Spawning directly observed" koos. Niet opgeslagen: zo past de score zich aan
 * zodra er iets bijkomt of verandert.
 */
export const eggspeditionReliability = (event) => {
  const eggs = [event, ...(event.child_events || [])].flatMap(moment => moment.occurrences || [])
  const any = (test) => eggs.some(test)

  const spawning = event.upload_role === "diver" || event.sampling_protocol === SPAWNING_METHODS[0]
  const dna = any(egg => (egg.dna || []).length > 0)
  const diameter = any(egg => (egg.measurements || []).some(m => m.measurement_type === "eggDiameter"))
  const ai = any(egg => (egg.identifications || []).some(i => i.identification_method === "ai"))

  if (spawning && dna) return 5
  if (spawning || dna) return 4
  if (diameter && ai) return 3
  return ai ? 2 : 1
}

export const eggspeditionToObservation = (event) => {
  if (!event) return null

  const occurrence = event.occurrences?.[0] || {}
  const hasCoordinates = event.decimal_latitude !== null && event.decimal_latitude !== undefined
    && event.decimal_longitude !== null && event.decimal_longitude !== undefined

  return {
    // Identificatie. `id` is bewust het event-id: dat is wat de routes als
    // `upload_id` doorgeven en waar de detailpagina mee ophaalt.
    id: event.event_id,
    event_id: event.event_id,
    occurrence_id: occurrence.occurrence_id || null,
    organism_id: occurrence.organism_id || null,
    user_id: event.user_id || null,
    specialist: event.upload_role || null,

    // Oude veldnamen, zodat de bestaande weergave niets merkt.
    datetime: event.event_date ? new Date(event.event_date).getTime() : null,
    date: event.event_date || null,
    location: hasCoordinates
      ? { type: "Point", coordinates: [event.decimal_longitude, event.decimal_latitude] }
      : null,
    depth: event.minimum_depth_in_meters ?? null,
    current: event.water_current ?? null,
    dive_site_id: event.dive_site_id ?? null,
    specimen_number: event.specimen_number ?? null,
    species: occurrence.scientific_name ?? null,
    species_id: occurrence.scientific_name_id ?? null,
    number_of_individuals: occurrence.individual_count ?? null,
    additional_information: occurrence.additional_information ?? null,
    egg_larvae_age: occurrence.egg_larvae_age ?? null,
    life_stage: occurrence.life_stage ?? null,
    tank_id: event.tank_id ?? null,
    attachment_type: occurrence.multimedia?.[0]?.attachment_category ?? null,

    // DNA hangt aan de occurrence en is een *lijst*, net als de egg trail kunnen er meerdere
    // resultaten zijn (een tweede lab, een herhaalde sequentie). `dna_results` is die volledige
    // lijst; de drie platte sleutels eronder zijn de meest recente rij, in de vorm waarin het
    // formulier ze verwacht. `dna_identified` bestaat nergens in de API, dat de melder "ja" zei
    // is precies wat het bestaan van een DNA-rij betekent, dus dat leiden we hier af, anders
    // klappen de `showWhen`-takken van het scientist-formulier in view/edit nooit open.
    // Over de hele waarneming, niet alleen de eerste occurrence, zie `eggspeditionDnaResults`.
    dna_results: eggspeditionDnaResults(event),
    reliability: eggspeditionReliability(event),
    identifications: occurrence.identifications || [],
    dna: occurrence.dna?.[0]?.dna_sequence ?? null,
    dna_sequence: occurrence.dna?.[0]?.dna_sequence ?? null,
    dna_barcode: occurrence.dna?.[0]?.target_gene ?? null,
    dna_identified: occurrence.dna?.length > 0 ? "Yes" : "No",

    // Een spawning batch: meerdere eieren uit één waargenomen paaiing, elk een occurrence onder dit
    // event. De velden hierboven gaan over het eerste ei; dit zijn ze allemaal, kort.
    batch_eggs: (event.occurrences || []).map(egg => ({
      occurrence_id: egg.occurrence_id,
      species: egg.scientific_name ?? null,
      species_id: egg.scientific_name_id ?? null,
      attachment_type: egg.multimedia?.[0]?.attachment_category ?? null,
      photos: (egg.multimedia || []).filter(media => media.file_url)
        .map(media => ({ src: media.file_url, format: media.format ?? null })),
      dna_count: egg.dna?.length || 0
    })),

    // `sampling_protocol` draagt sinds v1.13 het antwoord op "is het paaien waargenomen", maar
    // alleen als de melder het heeft ingevuld. Zonder override staat daar de rol-default
    // ("Scientific field sampling" en dergelijke), en dat is geen antwoord op die vraag. Vandaar
    // dat alleen de twee bekende opties terugkomen; al het andere blijft leeg.
    spawning_method: SPAWNING_METHODS.includes(event.sampling_protocol) ? event.sampling_protocol : null,
    // Het formulier vraagt het paaimoment als "zoveel uur vóór de foto"; terug naar die vorm.
    spawning_datetime: event.spawning_date && event.event_date
      ? Math.round((new Date(event.event_date) - new Date(event.spawning_date)) / 3_600_000)
      : null,
    consent_data_use: event.consent?.data_use ?? false,
    consent_public_access: event.consent?.public_access ?? false,
    consent_tos: event.consent?.terms_of_service ?? false,
    consent_email: event.consent?.contact_by_email ?? false,
    weather: eggspeditionWeatherToLegacy(event.weather),

    attachments: (occurrence.multimedia || []).map(media => ({
      id: media.multimedia_id,
      upload_id: event.event_id,
      multimedia_id: media.multimedia_id,
      occurrence_id: media.occurrence_id,
      attachment_type: media.attachment_category ?? null,
      file_url: media.file_url ?? null,
      // De naam waaronder het bestand is aangeleverd, en zijn mimetype ("image/jpeg"). `type` op de
      // rij zelf is de Darwin Core-term ("StillImage") en zegt niet of het beeld of video is.
      title: media.title ?? null,
      format: media.format ?? null,
      // `{specnr}_{siteID}_{DD}_{MM}_{YYYY}_{HHMM}_{framenr}.ext`, of `null` bij een waarneming
      // zonder duiklocatie, dan is er niets om de naam op te baseren.
      eggspedition_name: media.eggspedition_name ?? null,
      // De oude API hing de laatste voorspelling aan de bijlage. In de nieuwe
      // helft is een voorspelling een identificatie met methode `ai`; zie
      // eggspeditionLatestPrediction.
      prediction: null
    })),

    // Nieuw, en alleen aanwezig bij deze endpoints.
    occurrences: eggspeditionTrailOccurrences(event),
    child_events: event.child_events || [],
    event
  }
}

// Het tweede stuk van de naam: de duiklocatie als 4-lettercode, of, in een aquarium, het tanknummer
// met nullen ervoor tot vier tekens (20 wordt `0020`).
// Waar de waarneming is gedaan: de 4-lettercode van de duiklocatie, of in een aquarium het
// tanknummer met nullen ervoor tot vier cijfers (20 wordt `0020`).
const attachmentNamePlace = ({ siteCode, tankId }) => {
  if (siteCode) return siteCode
  if (tankId == null || tankId === "") return null

  return String(tankId).padStart(4, "0")
}

/**
 * Eén regel voor de batch-analyse: wat die pagina van een foto moet weten.
 *
 * Op een plek gedefinieerd omdat er twee kanten naartoe leiden, het overzicht met een selectie en
 * de bedankdialoog na een upload, en de batchpagina van beide dezelfde vorm moet krijgen.
 */
export const eggspeditionBatchItem = (observation, attachment) => ({
  multimedia_id: attachment?.id ?? attachment?.multimedia_id ?? null,
  file_url: attachment?.file_url ?? null,
  eggspedition_name: attachment?.eggspedition_name ?? null,
  observation_id: observation?.id ?? null,
  datetime: observation?.datetime ?? null,
  species: observation?.species ?? null
})

/** Alle bijlagen van één waarneming als batchregels; bijlagen zonder id vallen af. */
export const eggspeditionBatchItems = (observation) => (observation?.attachments || [])
  .filter(attachment => attachment?.id || attachment?.multimedia_id)
  .map(attachment => eggspeditionBatchItem(observation, attachment))

/**
 * Welk bouwsteentje de voorspelde bijlagenaam nog mist: `"datetime"`, `"place"`, `"specimen"`, of
 * `null` als hij compleet is.
 *
 * Bestaat omdat "er is geen naam" verschillende dingen kan betekenen, en de gebruiker daar
 * verschillende dingen aan kan doen. Zonder dit onderscheid staat er "vul de waarneming eerst in"
 * bij iemand die dat net heeft gedaan.
 */
export const eggspeditionAttachmentNameMissing = ({ specimenNumber, siteCode, tankId, datetime }) => {
  if (!datetime || Number.isNaN(new Date(datetime).getTime())) return "datetime"
  if (!siteCode && (tankId == null || tankId === "")) return "place"
  if (specimenNumber == null) return "specimen"

  return null
}

/**
 * De naam die de API voor een bijlage zal maken, vooruit berekend.
 *
 * Vorm: `{specnr}_{siteID|tankID}_{DD}_{MM}_{YYYY}_{HHMM}_{framenr}.ext`, zie
 * api/eggspedition_attachment_naam.md. Bedoeld om te *tonen* vóór het versturen, niet om mee te
 * sturen: de API bepaalt de definitieve naam zelf bij het opslaan.
 *
 * Twee redenen waarom dit een verwachting is en geen feit. Het specimennummer komt uit een
 * preview-endpoint dat niets reserveert, dus een tweede melder op dezelfde plek kan er tussen
 * komen. En het framenummer telt de bijlagen die al op de occurrence staan; bij een nieuwe
 * waarneming zijn dat er nul, maar dat weet je pas zeker als hij bestaat.
 *
 * Geeft `null` zodra een van de bouwstenen ontbreekt; dat is precies wanneer de API hem ook leeg
 * laat.
 */
export const eggspeditionAttachmentName = ({ specimenNumber, siteCode, tankId, datetime, frame, fileName }) => {
  const place = attachmentNamePlace({ siteCode, tankId })
  if (specimenNumber == null || !place || !datetime || !frame) return null

  const at = new Date(datetime)
  if (Number.isNaN(at.getTime())) return null

  const pad = (value) => String(value).padStart(2, "0")
  const stamp = [
    pad(at.getDate()),
    pad(at.getMonth() + 1),
    at.getFullYear(),
    `${pad(at.getHours())}${pad(at.getMinutes())}`
  ].join("_")

  // De extensie komt van het gekozen bestand; zonder punt in de naam is er niets te vermoeden.
  const extension = fileName && fileName.includes(".") ? fileName.split(".").pop().toLowerCase() : null

  return `${specimenNumber}_${place}_${stamp}_${frame}${extension ? `.${extension}` : ""}`
}

/**
 * De velden van het uploadformulier omzetten naar EggspeditionUploadBody.
 *
 * Het formulier houdt de oude sleutels aan (`datetime`, `number_of_individuals`,
 * `current`, `attachment_type`); die worden hier hernoemd. Verder: `datetime` is
 * een epoch-getal en wordt een ISO-tijdstempel, en `tank_id` mag zowel een id
 * als een heel tankobject zijn, bij het bekijken van een waarneming staat daar
 * het object in.
 */
export const eggspeditionUploadBody = (values, weather) => {
  const tank = values?.tank_id

  // Het DNA-blok gaat mee in dezelfde aanroep als de upload zelf. Dat was eerder een tweede
  // request (POST /occurrence/{id}/dna) die apart kon mislukken terwijl de waarneming al stond.
  // Alleen meesturen als er iets in staat; een leeg blok zou een lege DNA-rij aanmaken.
  const dna = values?.dna_identified === "Yes"
    ? {
      target_gene: values.dna_barcode || null,
      dna_sequence: values.dna_sequence || null,
      // De soort is bij een DNA-bepaling de uitkomst daarvan, dus het gewone soortveld, een
      // tweede soortvraag in het DNA-blok zou hetzelfde twee keer vragen.
      scientific_name: values.species || null,
      scientific_name_id: values.species_id || null,
      lab: values.dna_lab || null,
      sequenced_at: values.dna_sequenced_at ? new Date(values.dna_sequenced_at).toISOString() : null,
      percent_identity: values.dna_percent_identity === "" || values.dna_percent_identity == null
        ? null
        : Number(values.dna_percent_identity)
    }
    : null

  return {
    consent: values?.consent,
    event_date: values?.datetime ? new Date(values.datetime).toISOString() : null,
    /**
     * Het moment waarop het paaien is gezien. Ligt vóór `event_date` (dat is de foto), en het
     * verschil tussen die twee is de leeftijd van het ei.
     *
     * Het formulier bewaart dat verschil in uren; hier wordt er een moment van gemaakt, want een
     * waarneming hoort een tijd te dragen en geen rekensom.
     */
    spawning_date: typeof values?.spawning_datetime === "number" && values?.datetime
      ? new Date(new Date(values.datetime).getTime() - values.spawning_datetime * 3600000).toISOString()
      : null,
    location: values?.location ? { lat: values.location.lat, lng: values.location.lng } : null,
    depth: values?.depth ?? null,
    tank_id: tank && typeof tank === "object" ? tank.id : (tank ?? null),
    species: values?.species || null,
    species_id: values?.species_id || null,
    individual_count: values?.number_of_individuals ?? null,
    life_stage: values?.life_stage || "egg",
    water_current: values?.current || null,
    additional_information: values?.additional_information || null,
    // Uit de ΔT-kiezer komt een getal in uren; de API bewaart de eigen
    // bewoording van de melder als tekst.
    // Wie het paaien zag geeft geen schatting maar het moment zelf; de leeftijd is dan het verschil
    // met de foto. Beide wegen vullen hetzelfde veld, want het is dezelfde grootheid.
    egg_larvae_age: typeof values?.spawning_datetime === "number"
      ? formatHoursDuration(values.spawning_datetime)
      : (typeof values?.egg_larvae_age === "number"
        ? formatHoursDuration(values.egg_larvae_age)
        : (values?.egg_larvae_age || null)),
    dna_sequence: values?.dna_sequence || null,
    attachment_category: values?.attachment_type || null,
    // De duiklocatie is de **uuid** uit de lijst, niet de 4-lettercode. Bij een aquarist genegeerd
    // door de API (die zit in een tank, niet op een duiklocatie).
    dive_site_id: values?.dive_site_id || null,
    // Het antwoord op "is het paaien waargenomen" overschrijft de rol-default van dit event.
    sampling_protocol: values?.spawning_method || null,
    ...(dna ? { dna } : {}),
    weather: eggspeditionWeatherFromLegacy(weather),
    attachments: values?.attachments || []
  }
}

// De lamptijden van een tank: het formulier en de tankdetails rekenen in
// milliseconden-sinds-middernacht (zo staat het in het oude schema), de API
// bewaart ze als tijdstip op de dag ("07:00:00"). Deze twee zetten dat om.
//
// Dit is niet vrijblijvend: een getal in een tijdveld werd door de API stil
// gelezen als een unix-tijdstempel, en dan werd 07:00 opeens 1970-10-19. Sinds
// v1.7 is het kolomtype `time` en weigert de API een getal, maar de omzetting
// hoort hier te gebeuren, niet daar.
const msSindsMiddernachtNaarTijd = (ms) => {
  if (ms === null || ms === undefined || ms === "") return null

  const seconden = Math.floor(Number(ms) / 1000)
  if (!Number.isFinite(seconden)) return null

  const deel = (waarde) => String(waarde).padStart(2, "0")
  return `${deel(Math.floor(seconden / 3600))}:${deel(Math.floor(seconden / 60) % 60)}:${deel(seconden % 60)}`
}

const tijdNaarMsSindsMiddernacht = (tijd) => {
  if (tijd === null || tijd === undefined || tijd === "") return null

  const [uren, minuten, seconden] = String(tijd).split(":")
  return ((Number(uren) || 0) * 3600 + (Number(minuten) || 0) * 60 + Math.floor(Number(seconden) || 0)) * 1000
}

/**
 * Tank van de nieuwe (platte) naar de oude (geneste) vorm. Het formulier en de
 * tankweergave werken met `water_parameters` en `moonlight_parameters`.
 */
export const eggspeditionTankToLegacy = (tank) => {
  if (!tank) return tank

  return {
    id: tank.id,
    organization_name: tank.organization_name,
    tank_name: tank.tank_name,
    water_parameters: {
      temperature: tank.water_temperature,
      salinity: tank.water_salinity
    },
    moonlight_parameters: {
      light_outside: tank.light_outside,
      light_artificial: tank.light_artificial,
      ...(tank.light_artificial && {
        light_artificial_parameters: {
          brand: tank.light_artificial_brand,
          intensity: tank.light_artificial_intensity,
          intensity_unit: tank.light_artificial_unit,
          timespan_begin: tijdNaarMsSindsMiddernacht(tank.light_artificial_timespan_begin),
          timespan_end: tijdNaarMsSindsMiddernacht(tank.light_artificial_timespan_end)
        }
      })
    },
    species: tank.species || [],
    season_information: tank.season_information ?? null,
    additional_information: tank.additional_information ?? null
  }
}

/** En terug: de geneste tank-payload van het formulier naar de platte body. */
export const eggspeditionTankBody = (tank) => {
  if (!tank) return tank

  const artificial = tank.moonlight_parameters?.light_artificial_parameters

  return {
    ...(tank.id ? { id: tank.id } : {}),
    organization_name: tank.organization_name,
    tank_name: tank.tank_name,
    water_temperature: tank.water_parameters?.temperature ?? 0,
    water_salinity: tank.water_parameters?.salinity ?? 0,
    light_outside: Boolean(tank.moonlight_parameters?.light_outside),
    light_artificial: Boolean(tank.moonlight_parameters?.light_artificial),
    light_artificial_brand: artificial?.brand ?? null,
    light_artificial_intensity: artificial?.intensity ?? null,
    light_artificial_unit: artificial?.intensity_unit ?? null,
    light_artificial_timespan_begin: msSindsMiddernachtNaarTijd(artificial?.timespan_begin),
    light_artificial_timespan_end: msSindsMiddernachtNaarTijd(artificial?.timespan_end),
    species: tank.species || [],
    season_information: tank.season_information ?? null,
    additional_information: tank.additional_information ?? null
  }
}

/**
 * Eén identificatie met methode `ai` terugbrengen tot de vorm waarin de oude API
 * een voorspelling teruggaf: `{ id, attachment_id, predicted_at, prediction }`.
 * De analysepagina leest `prediction.prediction`; dat blijft zo werken.
 */
export const eggspeditionToPrediction = (identification) => {
  if (!identification?.raw_result) return null

  return {
    id: identification.identification_id,
    attachment_id: identification.source_multimedia_id,
    predicted_at: identification.date_identified,
    prediction: identification.raw_result
  }
}

/**
 * De laatste AI-voorspelling van een occurrence, eventueel beperkt tot één foto.
 *
 * De identificatiegeschiedenis is append-only en komt nieuwste eerst binnen, dus
 * de eerste treffer is de meest recente. De oude API bewaarde per bijlage één
 * voorspelling en overschreef die; hier staan ze allemaal nog, wat betekent dat
 * "de vorige uitslag" nu een keuze is in plaats van het enige wat er is.
 */
export const eggspeditionLatestPrediction = (occurrence, multimediaId) => {
  const identification = (occurrence?.identifications || []).find(i =>
    i.identification_method === "ai"
    && i.raw_result
    && (!multimediaId || i.source_multimedia_id === multimediaId))

  return eggspeditionToPrediction(identification)
}

/**
 * De voorspelling uit het antwoord van `EggspeditionPredictionGet` halen. Dat
 * antwoord is een identificatie-uitslag (`became_current` zegt of hij het
 * oordeel van een expert of DNA niet overrulet), met de ruwe uitslag in de
 * geschiedenis van de meegeleverde occurrence.
 */
export const eggspeditionPredictionFromResult = (result) => {
  if (!result) return null

  const identification = (result.occurrence?.identifications || [])
    .find(i => i.identification_id === result.identification_id)

  return eggspeditionToPrediction(identification)
}

/**
 * De occurrence die je volgt als je een vervolgmoment toevoegt: het eerste
 * specimen van de oorspronkelijke waarneming.
 *
 * Geeft `null` bij een waarneming die van de oude endpoints komt, die kent
 * geen occurrences, alleen een plat `id`. Dat onderscheid is belangrijk: dat
 * `id` doorgeven aan `/follow-up` zou een 404 opleveren, of erger, verwijzen
 * naar een occurrence van iemand anders.
 */
export const eggspeditionFollowUpSource = (observation) => {
  if (!observation) return null
  if (observation.occurrence_id) return observation.occurrence_id

  return observation.occurrences?.[0]?.occurrence_id || null
}

/**
 * Het laatste moment van de egg trail: de occurrence waar een nieuw vervolgmoment
 * logisch op aansluit.
 *
 * `eggspeditionTrailOccurrences` levert de trail al op tijdsvolgorde, dus dat is de
 * laatste uit de lijst. Bevat per occurrence ook `event_date` en `delta_hours` (uren
 * sinds t0), precies wat je nodig hebt om een nieuwe stap te tonen ten opzichte van
 * het vorige moment terwijl je hem als ΔT vanaf t0 verstuurt, wat de API verwacht.
 *
 * Geeft `null` bij een waarneming zonder trail (of uit de oude endpoints).
 */
export const eggspeditionTrailTail = (observation) => {
  const trail = observation?.occurrences || []
  return trail.length > 0 ? trail[trail.length - 1] : null
}
